import React, {memo, useEffect} from 'react';
import styles from './styles.module.scss';
import useNotifications from "./slice";
import {TextMedium16, ThirtyText} from "@/app/components/texts";
import {t} from "i18next";
import {s} from "@/app/utils/s";
import UserAvatar from "@/app/components/user_avatar/widget";
import {InvitationStatusEnum} from "@/app/api/invitation";
import Button from "@/app/components/button/widget";
import MatchConflictDialog from "./components/match_conflict_dialog";
import {useCookies, CookiesKey} from "@/app/utils/cookies";
import { logEventSafe } from "@/app/utils/analytics";

const NotificationsPage = () => {
  const {state, acceptInvitation, declineInvitation, getInvitationById, toggleMatchConflictWarning} = useNotifications();
  const {setCookie} = useCookies();

  // Save timestamp when user visits notifications page
  useEffect(() => {
    const now = new Date().getTime().toString();
    setCookie(CookiesKey.lastNotificationVisit, now, { path: '/', expires: 365 * 24 }); // 1 year in hours
    // Log open event
    logEventSafe('padel_notification_open');
  }, [setCookie]);

  const renderNotification = (n: any) => {
    const invitation = n.invitationId ? getInvitationById(n.invitationId) : null;
    const showButtons = invitation && invitation.status === InvitationStatusEnum.PENDING;
    const isAcceptButtonLoading = state.acceptButtonLoading[n.invitationId] || false;
    const isDeclineButtonLoading = state.declineButtonLoading[n.invitationId] || false;

    return (
      <div key={n.id} className={styles.notification}>
        <div className={styles.avatar}>
          <UserAvatar userAvatar={n.userImage} userName={n.userId?.toString() ?? ""}/>
        </div>
        <div className={styles.content}>
          <TextMedium16 text={n.text} className={styles.text}/>
          {showButtons && (
            <div className={styles.buttons}>
              <Button 
                text={t(s.accept)}
                onClick={() => acceptInvitation(n.invitationId)}
                isGreen
                isLoading={isAcceptButtonLoading}
                isBlackLoading
                className={styles.buttonsInNotification}
              />
              <Button 
                text={t(s.decline)}
                onClick={() => declineInvitation(n.invitationId)}
                isRed
                isOutline
                isLoading={isDeclineButtonLoading}
                isBlackLoading
                className={styles.buttonsInNotification}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.notifications}>
      {state.groupedNotifications.today.length > 0 && <div className={styles.group}>
          <ThirtyText text={t(s.today)} className={styles.title}/>
        {state.groupedNotifications.today.map(renderNotification)}
      </div>}
      {state.groupedNotifications.yesterday.length > 0 && <div className={styles.group}>
          <ThirtyText text={t(s.yesterday)} className={styles.title}/>
        {state.groupedNotifications.yesterday.map(renderNotification)}
      </div>}
      {state.groupedNotifications.lastSevenDays.length > 0 && <div className={styles.group}>
          <ThirtyText text={t(s.sevenDaysBefore)} className={styles.title}/>
        {state.groupedNotifications.lastSevenDays.map(renderNotification)}
      </div>}
      {state.groupedNotifications.lastThirtyDays.length > 0 && <div className={styles.group}>
          <ThirtyText text={t(s.last30Days)} className={styles.title}/>
        {state.groupedNotifications.lastThirtyDays.map(renderNotification)}
      </div>}
      <MatchConflictDialog
        isOpen={state.showMatchConflictWarning}
        onClose={toggleMatchConflictWarning}
      />
    </div>
  );
};

export default memo(NotificationsPage);
